import {gql} from '@apollo/client';

export const GET_ASSET_CLASSES = gql`
  query GetAssetClasses($input: AssetClassFilter) {
    getAssetClasses(input: $input) {
      ... on Error {
        message
        statusCode
      }
      ... on ResponseWithAssetClasses {
        pagination {
          total
        }
        data {
          id
          name
          key
          description
          status
          author

          language
        }
      }
    }
  }
`;

export const GET_ASSET_CLASS_LANGUAGES = gql`
  query GetAssetClassLanguageVersions($getAssetClassLanguageVersionsId: ID!) {
    getAssetClassLanguageVersions(id: $getAssetClassLanguageVersionsId) {
      ... on ResponseWithAssetClassLanguageVersions {
        data {
          description
          id
          languageVersions
          name
        }
      }
      ... on Error {
        message
        statusCode
      }
    }
  }
`;

export const GET_ASSET_CLASS = gql`
  query GetAssetClass($getAssetClassId: ID!, $language: LanguageEnum!) {
    getAssetClass(id: $getAssetClassId, language: $language) {
      ... on Error {
        message
        statusCode
      }
      ... on ResponseWithAssetClass {
        message
        data {
          id
          name
          key
          description
          status
          author
          instruments {
            name
            key
            description
            status
          }
          language
        }
      }
    }
  }
`;

/**
 * The order desk's instrument picker.
 *
 * GET_ASSET_CLASS_INSTRUMENTS serves a dozen screens and carries far more than
 * an order needs, while still missing the two fields the Create Transaction
 * modal depends on: `fractionalShares`, which decides whether a fractional unit
 * is allowed, and `feeSchedule`, without which the estimated fee always renders
 * as zero. This selects exactly what placing an order needs, per instrument
 * type — each quotes its unit price in a different field.
 */
export const GET_ORDER_DESK_INSTRUMENTS = gql`
  query GetOrderDeskInstruments($input: FinancialInstrumentFilter!) {
    getFinancialInstruments(input: $input) {
      ... on Error {
        message
        statusCode
      }
      ... on ResponseWithFinancialInstruments {
        data {
          ... on PublicEquity {
            id
            name
            ticker
            status
            fractionalShares
            currency {
              currencyCode
            }
            pricing {
              unitPrice
            }
            feeSchedule {
              feeName
              feeType
              fixedFeeAmount
              variableFeeAmount
            }
          }
          ... on Ipo {
            id
            name
            ticker
            status
            fractionalShares
            offerPrice
            currency {
              currencyCode
            }
            feeSchedule {
              feeName
              feeType
              fixedFeeAmount
              variableFeeAmount
            }
          }
          ... on Bond {
            id
            name
            ticker
            status
            fractionalShares
            currentMarketPrice
            currency {
              currencyCode
            }
            feeSchedule {
              feeName
              feeType
              fixedFeeAmount
              variableFeeAmount
            }
          }
          ... on MutualFund {
            id
            name
            ticker
            status
            fractionalShares
            currentNetAssetValue
            currency {
              currencyCode
            }
            feeSchedule {
              feeName
              feeType
              fixedFeeAmount
              variableFeeAmount
            }
          }
        }
        pagination {
          total
        }
      }
    }
  }
`;

export const GET_ASSET_CLASS_INSTRUMENTS = gql`
  query Bond($input: FinancialInstrumentFilter!) {
    getFinancialInstruments(input: $input) {
      ... on ResponseWithFinancialInstruments {
        data {
          ... on Instruments {
            title
            description
            riskLevel
            instrumentKey
            exclusive
            banner
            lang: language
            # Every stored language version, base included — the group table
            # reports how many an investor could be served.
            multilang {
              language
              title
              description
            }
            assetClassKey
            groupId: id
            instruments {
              ... on Bond {
                exchange {
                  id
                  code
                  stockExchangeName
                }
                id
                __typename
              }
              __typename
            }
            instruments {
              ... on MutualFund {
                exchange {
                  id
                  code
                  stockExchangeName
                }
                id
                __typename
              }
              __typename
            }
            description
            multilang {
              title
              language
              description
              __typename
            }
            __typename
          }
          ... on Bond {
            exchange {
              id
              code
              stockExchangeName
            }
            name
            marketPhase
            riskLevel
            key
            language
            ticker
            listingType
            currentMarketPrice
            inventoryPosition {
              autoBuyThresholdAmount
              enabledForTrading
              availableUnits
              averageUnitCost
              createdAt
              instrumentId
              healthPercentage
              id
              instrumentKey
              lowUnitThreshold
              __typename
            }
            id
            issuer {
              id
              __typename
            }
            status
            currency {
              currencyCode
              __typename
            }
            __typename
          }
          ... on PublicEquity {
            id
            name
            ticker
            icon
            status
            marketPhase
            riskLevel
            listingType
            sharesOutstanding
            listedAt
            currency {
              currencyCode
            }
            exchange {
              id
              code
            }
            pricing {
              unitPrice
              priceModel
              changePercent
            }
            ranking {
              position
              outOf
              basis
            }
            __typename
          }
          ... on Ipo {
            id
            name
            ticker
            icon
            status
            marketPhase
            riskLevel
            listingType
            ipoStatus
            listedInstrumentId
            offerPrice
            subscriptionStartDate
            subscriptionEndDate
            expectedListingDate
            currency {
              currencyCode
            }
            exchange {
              id
              code
            }
            offering {
              subscriptionSnapshot {
                percentSubscribed
                investorCount
              }
            }
            __typename
          }
          ... on MutualFund {
            exchange {
              id
              code
              stockExchangeName
            }
            name
            marketPhase
            riskLevel
            key
            id
            currentNavDate
            ticker
            currentNetAssetValue
            netAssetValueAtInception
            inceptionDate
            inventoryPosition {
              autoBuyThresholdAmount
              enabledForTrading
              availableUnits
              averageUnitCost
              createdAt
              instrumentId
              healthPercentage
              id
              instrumentKey
              lowUnitThreshold
              __typename
            }
            issuer {
              id
              __typename
            }
            status
            currency {
              currencyCode
              __typename
            }
            listingType
            listingType
            __typename
          }
          __typename
        }
        pagination {
          total
          __typename
        }
        __typename
      }
      __typename
    }
  }
`;

export const INSTRUMENTS_DETAILS_QUERIES = gql`
  query GetAllInstrumentsQueries {
    getAllCompanies {
      ... on Error {
        message
        statusCode
      }
      ... on Company {
        id
        name
        legalName
        ticker
        stage
        isTrending
        logoImgURL
        totalCapitalRaised
      }
    }
    getAllTopics {
      ... on Error {
        message
        statusCode
      }
      ... on Topic {
        id
        name
        icon
      }
    }
    getAllIndustries {
      ... on Error {
        message
        statusCode
      }
      ... on Industry {
        id
        name
        icon
      }
    }
  }
`;

export const GET_ALL_RATINGS = gql`
  query ResponseWithRatings($page: Int!, $limit: Int!) {
    getAllRatings(page: $page, limit: $limit) {
      ... on ResponseWithRatings {
        message
        data {
          id
          agencyName
          agencyLogo
          rating
          document
          type
        }
      }
      ... on Error {
        message
        statusCode
      }
    }
  }
`;

export const GET_RATING_BY_ID = gql`
  query GetRatingById($getRatingByIdId: ID!) {
    getRatingById(id: $getRatingByIdId) {
      ... on ResponseWithRating {
        data {
          id
          agencyName
          agencyLogo
          rating
          document
          type
        }
      }
      ... on Error {
        message
        statusCode
      }
    }
  }
`;

/**
 * Per-language review state for one instrument.
 *
 * `languageVersions` alone was never enough to run a review: a machine translation is
 * persisted immediately (DeepL bills per character, so a dropped session must not cost a
 * second call) but lands PENDING_REVIEW and is NOT served to mobile until an admin approves
 * it. Selecting only the language list meant the screen showed an unpublished draft and an
 * approved translation identically, with no way to tell them apart or to publish one.
 *
 * `stale` and `safeToRetranslate` are derived server-side from `sourceUpdatedAt` versus each
 * row's `sourceVersionAt`, so they cannot drift out of agreement with the data.
 */
export const GET_FINANCIAL_INSTRUMENT_LANGUAGE_VERSIONS = gql`
  query GetFinancialInstrumentLanguageVersions(
    $getFinancialInstrumentLanguageVersionsId: ID!
    $assetClassKey: AssetClassKey!
  ) {
    instrument: getFinancialInstrument(
      id: $getFinancialInstrumentLanguageVersionsId
    ) {
      ... on Error {
        message
        statusCode
      }
      ... on ResponseWithFinancialInstrument {
        data {
          ... on Bond {
            key
          }
          ... on MutualFund {
            key
          }
          ... on PublicEquity {
            key
          }
          ... on Ipo {
            key
          }
          ... on PrivateEquity {
            key
          }
          ... on VentureFund {
            key
          }
        }
      }
    }
    getFinancialInstrumentLanguageVersions(
      id: $getFinancialInstrumentLanguageVersionsId
      assetClassKey: $assetClassKey
    ) {
      ... on Error {
        message
        statusCode
      }
      ... on ResponseWithFinancialInstrumentLanguageVersions {
        data {
          id
          name
          description
          language
          languageVersions
          sourceUpdatedAt
          translations {
            language
            translationSource
            reviewStatus
            reviewedBy
            reviewedAt
            sourceVersionAt
            stale
            safeToRetranslate
          }
        }
      }
    }
  }
`;

/**
 * The translated copy itself, for the review screen's preview.
 *
 * `getFinancialInstrument(id, language)` overlays the language row on the instrument and, for
 * an authenticated admin, will return a PENDING_REVIEW draft — that admin-only preview is the
 * whole point, since reviewing copy you cannot read is not reviewing. Reading a draft never
 * publishes it.
 *
 * The union needs one fragment per concrete type; the selections mirror the backend's
 * per-type translatable field list (translateInstrumentFields.ts), so anything the reviewer
 * is asked to approve is something they can actually see here.
 */
export const GET_INSTRUMENT_TRANSLATION_PREVIEW = gql`
  query GetInstrumentTranslationPreview($id: ID!, $language: LanguageEnum) {
    getFinancialInstrument(id: $id, language: $language) {
      ... on Error {
        message
        statusCode
      }
      ... on ResponseWithFinancialInstrument {
        data {
          ... on Ipo {
            id
            name
            description
            keyFacts {
              name
              value
            }
            disclosures {
              title
              description
              mandatory
            }
            highlights
            offering {
              useOfProceeds {
                description
                allocations {
                  allocationCategory
                  percentageValue
                }
              }
            }
          }
          ... on Bond {
            id
            name
            description
            keyFacts {
              name
              value
            }
            disclosures {
              title
              description
              mandatory
            }
          }
          ... on PublicEquity {
            id
            name
            description
            keyFacts {
              name
              value
            }
            disclosures {
              title
              description
              mandatory
            }
          }
          # PrivateEquity exposes no prose beyond the base pair. The backend's translation
          # config still lists benefits for this type, but the SDL has no such field, so
          # there is nothing to render and nothing a reviewer could check.
          ... on PrivateEquity {
            id
            name
            description
            keyFacts {
              name
              value
            }
            disclosures {
              title
              description
              mandatory
            }
          }
          ... on MutualFund {
            id
            name
            description
            keyFacts {
              name
              value
            }
            disclosures {
              title
              description
              mandatory
            }
            investmentStrategy
            benefits
            bestSuitedFor
          }
          ... on VentureFund {
            id
            name
            description
            keyFacts {
              name
              value
            }
            disclosures {
              title
              description
              mandatory
            }
            thesisAndInvestmentPhilosophy
            investmentStrategy
          }
        }
      }
    }
  }
`;

// Rewritten for the FinancialInstrument model (investing af46d866).
// Verified against the live dev gateway: every field below is accepted.
//
// Held back pending backend fixes — adding either one nulls the whole response:
//   type                       BondType enum rejects stored values GREEN and SUKUK
//   settings.publicAudience    throws on records that have a value set
export const GET_BOND_INSTRUMENT_BY_LANGUAGE = gql`
  query GetFinancialInstrument(
    $getFinancialInstrumentId: ID!
    $language: LanguageEnum
  ) {
    getFinancialInstrument(id: $getFinancialInstrumentId, language: $language) {
      ... on Error {
        statusCode
        message
      }
      ... on ResponseWithFinancialInstrument {
        data {
          ... on Bond {
            id
            name
            icon
            description
            language
            key
            status
            templateId
            assetClassKey
            listingType
            marketPhase
            riskLevel
            ticker
            isin
            countries {
              id
              name
            }
            startDate
            endDate
            issuer {
              id
              issuerType
            }
            currency {
              currencyCode
              symbol
              name
            }
            sectorIndustry {
              id
              name
            }
            topics {
              id
              name
            }
            settings {
              isPaused
              isVisible
              shouldNotifyUsersOnLaunch
            }
            shariaCompliant
            forDabaPro
            enabledForFreeSimulation
            tradeable
            useInventoryForTrade
            lotSize
            fractionalShares
            minTradeableUnits
            maxTradeableUnits
            minTradeableAmount
            maxTradeableAmount
            orderExpirationLimit
            allocatedUnits
            availableUnits
            unitsSold
            totalInvestedAmount
            totalInvestment
            tradingVolume
            keyFacts {
              name
              value
            }
            disclosures {
              title
              description
              mandatory
            }
            documents {
              name
              fileLink
            }
            invitedUsers {
              id
              firstName
              lastName
              email
            }
            invitedGroups {
              id
            }
            feeSchedule {
              feeName
              feeType
              fixedFeeAmount
              variableFeeAmount
              frequency
              origination
              scalable
              level
              perExchange
              vector {
                magnitude {
                  feeType
                  value
                }
                duration
              }
            }
            exchange {
              id
              code
              stockExchangeName
              logoImage
              baseCurrencyCode
            }
            pricing {
              unitPrice
              priceModel
              currency
              changeAbsolute
              changePercent
              asOf
              source
            }
            returns {
              period
              year
              metric
              value
              asOf
            }
            offering {
              useOfProceeds {
                description
                allocations {
                  allocationCategory
                  percentageValue
                }
              }
              parties {
                role
                name
                logo
                website
                description
                companyId
              }
              subscriptionSnapshot {
                percentSubscribed
                investorCount
                amountRaised
                source
                asOf
              }
              timeline {
                kind
                key
                label
                date
                endDate
                status
              }
              fundingTarget
              minimumFundingTarget
              maximumFundingTarget
              externalFunding {
                name
                amount
              }
            }
            insiderTake {
              firm
              rating
              note
              asOf
            }
            ranking {
              position
              outOf
              basis
            }
            maturityDate
            tenor
            couponRate
            callable
            currentMarketPrice
            yieldToMaturity
            issuanceDate
            couponFrequency
            gracePeriodPrincipal
            gracePeriodInterest
            managementFee
            nextCouponDate
            nextCallDate
            nextCallPrice
            issuanceVolume
            issueSizeUnits
            faceValue
            issuerType
            creditOutlook
            amountOutstanding
            realizedYield
            rating {
              id
              agencyName
              agencyLogo
              rating
              document
              type
            }
            schedule {
              kind
              key
              label
              date
              endDate
              amount
              percentOfPrincipal
              sequence
              status
            }
            legacyCampaignId
          }
        }
      }
    }
  }
`;

// Rewritten for the FinancialInstrument model (investing af46d866).
// Verified against the live dev gateway: every field below is accepted.
//
// Three fields were held back here because each nulled the whole response:
// `language` (non-nullable, stored null), `totalInvestedAmount` (returned
// "[object Object]0"), and `type` (BondType rejected the stored GREEN and
// SUKUK values), which is why `type` is still fetched on its own.
//
// All three were re-tested against the dev gateway on 2026-08-20 and are fixed.
// Folding them back into the main read is now safe but has not been done —
// keeping `type` isolated still means a regression costs only this one field.
export const GET_BOND_TYPE = gql`
  query GetBondType($getFinancialInstrumentId: ID!) {
    getFinancialInstrument(id: $getFinancialInstrumentId) {
      ... on Error {
        message
      }
      ... on ResponseWithFinancialInstrument {
        data {
          ... on Bond {
            id
            type
          }
        }
      }
    }
  }
`;

export const GET_MUTUAL_FUND_BY_LANGUAGE = gql`
  query GetFinancialInstrument(
    $getFinancialInstrumentId: ID!
    $language: LanguageEnum
  ) {
    getFinancialInstrument(id: $getFinancialInstrumentId, language: $language) {
      ... on Error {
        statusCode
        message
      }
      ... on ResponseWithFinancialInstrument {
        data {
          ... on MutualFund {
            id
            name
            icon
            description
            key
            status
            templateId
            assetClassKey
            listingType
            marketPhase
            riskLevel
            ticker
            isin
            countries {
              id
              name
            }
            startDate
            endDate
            updatedAt
            issuer {
              id
              issuerType
            }
            currency {
              currencyCode
              symbol
              name
            }
            sectorIndustry {
              id
              name
            }
            topics {
              id
              name
            }
            settings {
              isPaused
              isVisible
              shouldNotifyUsersOnLaunch
            }
            shariaCompliant
            forDabaPro
            enabledForFreeSimulation
            tradeable
            useInventoryForTrade
            lotSize
            fractionalShares
            minTradeableUnits
            maxTradeableUnits
            minTradeableAmount
            maxTradeableAmount
            orderExpirationLimit
            allocatedUnits
            availableUnits
            unitsSold
            totalInvestment
            tradingVolume
            keyFacts {
              name
              value
            }
            disclosures {
              title
              description
              mandatory
            }
            documents {
              name
              fileLink
            }
            invitedUsers {
              id
              firstName
              lastName
              email
            }
            invitedGroups {
              id
            }
            feeSchedule {
              feeName
              feeType
              fixedFeeAmount
              variableFeeAmount
              frequency
              origination
              scalable
              level
              perExchange
              vector {
                magnitude {
                  feeType
                  value
                }
                duration
              }
            }
            exchange {
              id
              code
              stockExchangeName
              logoImage
              baseCurrencyCode
            }
            pricing {
              unitPrice
              priceModel
              currency
              changeAbsolute
              changePercent
              asOf
              source
            }
            returns {
              period
              year
              metric
              value
              asOf
            }
            insiderTake {
              firm
              rating
              note
              asOf
            }
            ranking {
              position
              outOf
              basis
            }
            type
            currentNetAssetValue
            netAssetValueAtInception
            currentNavDate
            inceptionDate
            projectedAnnualReturn
            investmentStrategy
            investmentHorizon
            benefits
            bestSuitedFor
            fundStructure
            valuationFrequency
            settlementDaysOnRedemption
            allowsAutoInvest
            managedBy {
              role
              name
              logo
              website
              description
              companyId
            }
            assetAllocation {
              assetClass
              percent
            }
            benchmark {
              name
              comparison
              performance
              lastYearPerformance
              yearlyPerformance {
                year
                performance
              }
            }
          }
        }
      }
    }
  }
`;

export const LIST_MANAGED_FUND_INVENTORY_PREDICTION_RUNS = gql`
  query ListManagedFundInventoryPredictionRuns($filter: PredictionRunFilter) {
    listManagedFundInventoryPredictionRuns(filter: $filter) {
      ... on Error {
        message
        statusCode
        type
        errorTypeEnum
        retryAfter
      }
      ... on ResponseWithManagedFundInventoryPredictionRuns {
        message
        data {
          id
          runId
          runType
          triggeredBy
          startedAt
          completedAt
          status
          totalsByCurrency {
            currency
            totalAmount
            thresholdAmount
            highPriority
          }
          totalRecommendedAmountUsd
          highPriority
          runDate
          supersededBy
          supersededAt
          dataQuality
          createdAt
          updatedAt
        }
        pagination {
          page
          limit
          pages
          total
          hasPrevPage
          hasNextPage
          previous
          next
        }
      }
    }
  }
`;

export const GET_MANAGED_FUND_INVENTORY_PREDICTION_RESULTS = gql`
  query GetManagedFundInventoryPredictionResults($runId: String!) {
    getManagedFundInventoryPredictionResults(runId: $runId) {
      ... on Error {
        message
        statusCode
        type
        errorTypeEnum
        retryAfter
      }
      ... on ResponseWithManagedFundInventoryPredictionResults {
        message
        data {
          id
          runId
          ticker
          dabaName
          assetManager
          currency
          calculationMethod
          weeklyAvgDemand
          currentNetDemand
          currentInventory
          ongoingUnits
          quantityNeeded
          bufferRate
          adjustedUnits
          navPrice
          navDate
          orderAmount
          orderAmountUsd
          fxRateToUsd
          fxRateUpdatedAt
          recommendation
          createdAt
        }
      }
    }
  }
`;

export const LIST_MANAGED_FUND_INVENTORY_CONFIGS = gql`
  query ListManagedFundInventoryConfigs($activeOnly: Boolean) {
    listManagedFundInventoryConfigs(activeOnly: $activeOnly) {
      ... on Error {
        statusCode
        message
      }
      ... on ResponseWithManagedFundInventoryConfigs {
        message
        data {
          id
          ticker
          weeklyDivisor
          bufferRate
          active
          updatedBy
          updatedAt
        }
      }
    }
  }
`;

export const EXPORT_MANAGED_FUND_INVENTORY_PREDICTION_RUN = gql`
  query ExportManagedFundInventoryPredictionRun(
    $input: ExportManagedFundInventoryPredictionRunInput!
  ) {
    exportManagedFundInventoryPredictionRun(input: $input) {
      ... on Error {
        statusCode
        message
      }
      ... on Response {
        message
      }
    }
  }
`;

// Single-instrument reads for the two types added after the bond/fund work.
//
// These originally omitted `language` on the theory that it was a shape choice.
// It was not: `getFinancialInstrument(id, language)` is one field shared by
// every type — the bond and fund reads above already pass it — so leaving it
// out did not mean "no translations", it meant "always serve the base
// language". Opening the French row of an IPO from Language versions rendered
// the English `name` and `description`, with nothing on screen to say so.
export const GET_IPO_BY_ID = gql`
  query GetIpoInstrument(
    $getFinancialInstrumentId: ID!
    $language: LanguageEnum
  ) {
    getFinancialInstrument(id: $getFinancialInstrumentId, language: $language) {
      ... on Error {
        statusCode
        message
      }
      ... on ResponseWithFinancialInstrument {
        data {
          ... on Ipo {
            id
            name
            icon
            description
            key
            status
            assetClassKey
            listingType
            marketPhase
            riskLevel
            ticker
            isin
            countries {
              id
              name
            }
            startDate
            endDate
            issuer {
              id
              issuerType
            }
            currency {
              currencyCode
              symbol
              name
            }
            sectorIndustry {
              id
              name
            }
            topics {
              id
              name
            }
            settings {
              isPaused
              isVisible
              shouldNotifyUsersOnLaunch
            }
            shariaCompliant
            forDabaPro
            enabledForFreeSimulation
            tradeable
            useInventoryForTrade
            lotSize
            fractionalShares
            minTradeableUnits
            maxTradeableUnits
            orderExpirationLimit
            allocatedUnits
            availableUnits
            keyFacts {
              name
              value
            }
            disclosures {
              title
              description
              mandatory
            }
            documents {
              name
              fileLink
            }
            feeSchedule {
              feeName
              feeType
              fixedFeeAmount
              variableFeeAmount
              frequency
              origination
              scalable
              level
              perExchange
              vector {
                magnitude {
                  feeType
                  value
                }
                duration
              }
            }
            exchange {
              id
              code
              stockExchangeName
            }
            pricing {
              unitPrice
              priceModel
              currency
            }
            offerPrice
            priceRangeLow
            priceRangeHigh
            totalSharesOnOffer
            dabaAllocationShares
            ipoStatus
            offerAnnouncementDate
            subscriptionStartDate
            subscriptionEndDate
            allocationAnnouncementDate
            refundDate
            expectedListingDate
            sellUnlockDate
            tentativeDates
            exclusiveOnDaba
            fullAllocationLikely
            highlights
            listedInstrumentId
            listedAt
            provider
            providerOfferId
            offering {
              useOfProceeds {
                description
                allocations {
                  allocationCategory
                  percentageValue
                }
              }
              parties {
                role
                name
                logo
                website
                description
              }
              subscriptionSnapshot {
                percentSubscribed
                investorCount
                amountRaised
                asOf
              }
              timeline {
                kind
                key
                label
                date
                endDate
                status
              }
            }
          }
        }
      }
    }
  }
`;

export const GET_PUBLIC_EQUITY_BY_ID = gql`
  query GetPublicEquityInstrument(
    $getFinancialInstrumentId: ID!
    $language: LanguageEnum
  ) {
    getFinancialInstrument(id: $getFinancialInstrumentId, language: $language) {
      ... on Error {
        statusCode
        message
      }
      ... on ResponseWithFinancialInstrument {
        data {
          ... on PublicEquity {
            id
            name
            icon
            description
            key
            status
            assetClassKey
            listingType
            marketPhase
            riskLevel
            ticker
            isin
            countries {
              id
              name
            }
            startDate
            endDate
            issuer {
              id
              issuerType
            }
            currency {
              currencyCode
              symbol
              name
            }
            sectorIndustry {
              id
              name
            }
            topics {
              id
              name
            }
            settings {
              isPaused
              isVisible
              shouldNotifyUsersOnLaunch
            }
            shariaCompliant
            forDabaPro
            enabledForFreeSimulation
            tradeable
            useInventoryForTrade
            lotSize
            fractionalShares
            minTradeableUnits
            maxTradeableUnits
            orderExpirationLimit
            keyFacts {
              name
              value
            }
            disclosures {
              title
              description
              mandatory
            }
            documents {
              name
              fileLink
            }
            feeSchedule {
              feeName
              feeType
              fixedFeeAmount
              variableFeeAmount
              frequency
              origination
              scalable
              level
              perExchange
              vector {
                magnitude {
                  feeType
                  value
                }
                duration
              }
            }
            exchange {
              id
              code
              stockExchangeName
            }
            pricing {
              unitPrice
              priceModel
              changePercent
            }
            ranking {
              position
              outOf
              basis
            }
            listedAt
            sourceIpoId
            sharesOutstanding
            dividend {
              yield
              amountPerShare
              exDate
              nextDate
              lastDate
            }
            latestQuote {
              lastPrice
              openingPrice
              closingPrice
              highPrice
              lowPrice
              percentChange
              volume
              marketCapitalization
              baseCurrencyCode
              asOf
              source
            }
          }
        }
      }
    }
  }
`;

/* ---------------------------------------------------------------------------
   Options feed for the shared FinancialInstrumentPicker.

   Deliberately lean — id, label, icon and taxonomy only. The full instrument
   reads elsewhere in this file select dozens of fields; a type-ahead needs
   none of them, and one unknown field nulls the whole federated response.

   Covers all six keys the communication handover targets, including
   PrivateEquity and VentureFund, which the notification and announcement
   composers previously had no way to reach.

   Note `Ipo`, not `IPO` — the gateway spells it that way.
   --------------------------------------------------------------------------- */
export const GET_FINANCIAL_INSTRUMENT_OPTIONS = gql`
  query AdminFinancialInstrumentOptions($input: FinancialInstrumentFilter!) {
    getFinancialInstruments(input: $input) {
      ... on Error {
        message
        statusCode
      }
      ... on ResponseWithFinancialInstruments {
        pagination {
          page
          limit
          pages
          total
          hasNextPage
        }
        data {
          ... on PublicEquity {
            id
            name
            ticker
            icon
            key
            assetClassKey
            status
          }
          ... on Bond {
            id
            name
            ticker
            icon
            key
            assetClassKey
            status
          }
          ... on MutualFund {
            id
            name
            ticker
            icon
            key
            assetClassKey
            status
          }
          ... on Ipo {
            id
            name
            ticker
            icon
            key
            assetClassKey
            status
          }
          ... on PrivateEquity {
            id
            name
            ticker
            icon
            key
            assetClassKey
            status
          }
          ... on VentureFund {
            id
            name
            ticker
            icon
            key
            assetClassKey
            status
            offeringName
          }
        }
      }
    }
  }
`;

/* Resolves one instrument by id, for the shared picker.

   An edit form knows only the saved `instrumentId`. That instrument is very
   often outside the picker's first page of results, so without this the
   Select had a value but no matching option and rendered empty — which reads
   as "nothing was selected" and loses the destination on the next save. */
export const GET_FINANCIAL_INSTRUMENT_OPTION_BY_ID = gql`
  query AdminFinancialInstrumentOptionById($id: ID!) {
    getFinancialInstrument(id: $id) {
      ... on Error {
        message
        statusCode
      }
      ... on ResponseWithFinancialInstrument {
        data {
          ... on PublicEquity {
            id
            name
            ticker
            icon
            key
            status
          }
          ... on Bond {
            id
            name
            ticker
            icon
            key
            status
          }
          ... on MutualFund {
            id
            name
            ticker
            icon
            key
            status
          }
          ... on Ipo {
            id
            name
            ticker
            icon
            key
            status
          }
          ... on PrivateEquity {
            id
            name
            ticker
            icon
            key
            status
          }
          ... on VentureFund {
            id
            name
            ticker
            icon
            key
            status
            offeringName
          }
        }
      }
    }
  }
`;
